import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Avatar } from "@/components/ui/avatar"
import { Sparkline } from "@/components/ui/sparkline"
import { KpiCard } from "@/components/ui/kpi-card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

// /analytics/sla/team — full-team SLA scoreboard.
//
// Drill-down from the SLA Performance dashboard's "Team SLA performance"
// card. Backed by Portal::Analytics::Sla::Team::Presenter. Displays:
//   1. PageHeader
//   2. Range buttons (Today / 7d / 30d / 90d)
//   3. 4 summary KPI cards (FR %, Res %, Idle breaches, Avg TTFR)
//   4. Filter bar — search input (debounced) + CSV download button
//   5. Sortable + paginated handler table (Entity / FR / Res / Idle / TTFR / Tickets / Trend)

// ─── Types (camelCase, matches the presenter output) ─────────────────

type RangeKey = "today" | "7d" | "30d" | "90d" | "custom"

type KpiValue = {
  value: number | null
  delta: number | null
  deltaUnit: "pp" | "sec" | "abs"
  goal: number | null
  sparkline: (number | null)[]
}

type EntityRow = {
  id: string
  kind: "human"
  name: string
  avatarUrl?: string | null
  frAttainmentPct: number | null
  resAttainmentPct: number | null
  idleBreaches: number
  avgFirstResponseSeconds: number | null
  convosHandled: number
  sparkline: (number | null)[]
}

type SortKey =
  | "name"
  | "frAttainmentPct"
  | "resAttainmentPct"
  | "idleBreaches"
  | "avgFirstResponseSeconds"
  | "convosHandled"

export type SlaTeamProps = {
  range: { key: RangeKey; from: string; to: string }
  kpis: {
    firstResponseAttainmentPct: KpiValue
    resolutionAttainmentPct: KpiValue
    idleBreachesTotal: KpiValue
    avgFirstResponseSeconds: KpiValue
  }
  rows: EntityRow[]
  pagination: { page: number; perPage: number; totalRows: number; totalPages: number }
  filters: { q: string; sort: SortKey; dir: "asc" | "desc" }
}

const COLOR_FR   = "#ef4444"
const COLOR_RES  = "#fbbf24"
const COLOR_IDLE = "#9ca3af"

const RANGES: Array<{ key: RangeKey; label: string }> = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7d" },
  { key: "30d",   label: "30d" },
  { key: "90d",   label: "90d" },
  { key: "custom", label: "Custom" },
]

// ─── Page ────────────────────────────────────────────────────────────

export default function SlaTeam(props: SlaTeamProps) {
  // Defensive defaults — page must render even if any prop is missing.
  const range = props.range ?? { key: "30d" as RangeKey, from: "", to: "" }
  const kpis = props.kpis ?? ({} as SlaTeamProps["kpis"])
  const rows = props.rows ?? []
  const pagination = props.pagination ?? { page: 1, perPage: 50, totalRows: 0, totalPages: 1 }
  const filters = props.filters ?? { q: "", sort: "frAttainmentPct" as SortKey, dir: "asc" as const }

  const priorLabel = priorLabelFor(range.key)
  const frGoal = kpis.firstResponseAttainmentPct?.goal ?? null
  const resGoal = kpis.resolutionAttainmentPct?.goal ?? null

  const [searchInput, setSearchInput] = useState(filters.q || "")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounce 300ms — avoids hitting the server on every keystroke.
  useEffect(() => {
    if (searchInput === (filters.q || "")) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      navigate({ q: searchInput, page: 1 }, { replace: true })
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  function navigate(
    overrides: Partial<{ range: RangeKey; q: string; sort: SortKey; dir: "asc" | "desc"; page: number }>,
    opts: { replace?: boolean } = {},
  ) {
    const params = new URLSearchParams()
    const next = {
      range: overrides.range ?? range.key,
      q: overrides.q ?? filters.q ?? "",
      sort: overrides.sort ?? filters.sort,
      dir: overrides.dir ?? filters.dir,
      page: overrides.page ?? pagination.page,
    }
    if (next.range && next.range !== "30d") params.set("range", next.range)
    if (next.q) params.set("q", next.q)
    if (next.sort && next.sort !== "frAttainmentPct") params.set("sort", next.sort)
    if (next.dir && next.dir !== "asc") params.set("dir", next.dir)
    if (next.page && next.page > 1) params.set("page", String(next.page))
    const qs = params.toString()
    // preserveState keeps the search input mounted so it doesn't lose
    // focus on each debounced keystroke. replace=true (used by search)
    // collapses keystroke history so the back button only moves between
    // distinct user-meaningful states (range / sort / page).
    router.visit(`/analytics/sla/team${qs ? `?${qs}` : ""}`, {
      preserveState: true,
      preserveScroll: true,
      replace: opts.replace ?? false,
    })
  }

  function setRange(key: RangeKey) {
    navigate({ range: key, page: 1 })
  }

  function toggleSort(key: SortKey) {
    if (key === filters.sort) {
      navigate({ dir: filters.dir === "desc" ? "asc" : "desc", page: 1 })
      return
    }
    const dir: "asc" | "desc" =
      key === "avgFirstResponseSeconds" || key === "idleBreaches" || key === "frAttainmentPct" || key === "name"
        ? "asc"
        : "desc"
    navigate({ sort: key, dir, page: 1 })
  }

  function gotoPage(p: number) {
    if (p < 1 || p > pagination.totalPages) return
    navigate({ page: p })
  }

  const csvUrl = (() => {
    const params = new URLSearchParams()
    if (range.key && range.key !== "30d") params.set("range", range.key)
    if (filters.q) params.set("q", filters.q)
    const qs = params.toString()
    return `/analytics/sla/team.csv${qs ? `?${qs}` : ""}`
  })()

  const start = pagination.totalRows === 0 ? 0 : (pagination.page - 1) * pagination.perPage + 1
  const end = Math.min(pagination.page * pagination.perPage, pagination.totalRows)

  return (
    <AppShell>
      <PageHeader
        eyebrow="Analytics"
        title="Team SLA performance"
        subtitle="Per-handler attainment across all SLA clocks. AI agents are excluded — SLA only applies post-escalation."
      />

      <div className="space-y-3">
        {/* ─── Range buttons ───────────────────────────────────────── */}
        <div className="flex justify-end">
          <RangeSwitcher activeKey={range.key} onChange={setRange} />
        </div>

        {/* ─── KPI row (4 cards) ───────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <KpiCard
            label="First-response SLA attainment"
            kpi={kpis.firstResponseAttainmentPct}
            fillColor={COLOR_FR}
            formatValue={formatPct}
            goalCmp="gte"
            priorLabel={priorLabel}
            footnote={targetFootnote(kpis.firstResponseAttainmentPct?.goal, "gte", formatPct)}
          />
          <KpiCard
            label="Resolution SLA attainment"
            kpi={kpis.resolutionAttainmentPct}
            fillColor={COLOR_RES}
            formatValue={formatPct}
            goalCmp="gte"
            priorLabel={priorLabel}
            footnote={targetFootnote(kpis.resolutionAttainmentPct?.goal, "gte", formatPct)}
          />
          <KpiCard
            label="Idle breaches"
            kpi={kpis.idleBreachesTotal}
            fillColor={COLOR_IDLE}
            formatValue={formatCount}
            goalCmp="lte"
            invertDelta
            showDeltaPct
            sparklineVariant="bar"
            priorLabel={priorLabel}
            footnote={`Idle breaches in range: ${formatCount(kpis.idleBreachesTotal?.value ?? null)}`}
          />
          <KpiCard
            label="Avg. time to first response"
            kpi={kpis.avgFirstResponseSeconds}
            fillColor={COLOR_FR}
            formatValue={formatSeconds}
            goalCmp="lte"
            invertDelta
            priorLabel={priorLabel}
            footnote={targetFootnote(kpis.avgFirstResponseSeconds?.goal, "lte", formatSeconds)}
          />
        </div>

        {/* ─── Filter bar ──────────────────────────────────────────── */}
        <Card className="p-3">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md">
              <Input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by agent…"
                className="w-full"
              />
            </div>
            <a href={csvUrl}>
              <Button variant="ghost" size="sm">Download CSV</Button>
            </a>
          </div>
        </Card>

        {/* ─── Full table ──────────────────────────────────────────── */}
        <Card className="p-0 overflow-hidden">
          {rows.length === 0 ? (
            <div className="p-5 text-sm text-app-muted">
              {pagination.totalRows === 0 && !filters.q
                ? "No SLA-tracked handlers in this range."
                : "No handlers match your search."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-white/[0.02] text-[11px] uppercase tracking-wide text-app-subtle">
                    <SortHeader label="Entity"        onClick={() => toggleSort("name")}                    active={filters.sort === "name"}                    dir={filters.dir} align="left" />
                    <SortHeader label="FR %"          onClick={() => toggleSort("frAttainmentPct")}         active={filters.sort === "frAttainmentPct"}         dir={filters.dir} align="right" />
                    <SortHeader label="Res %"         onClick={() => toggleSort("resAttainmentPct")}        active={filters.sort === "resAttainmentPct"}        dir={filters.dir} align="right" />
                    <SortHeader label="Idle breaches" onClick={() => toggleSort("idleBreaches")}            active={filters.sort === "idleBreaches"}            dir={filters.dir} align="right" />
                    <SortHeader label="Avg TTFR"      onClick={() => toggleSort("avgFirstResponseSeconds")} active={filters.sort === "avgFirstResponseSeconds"} dir={filters.dir} align="right" />
                    <SortHeader label="Tickets"       onClick={() => toggleSort("convosHandled")}           active={filters.sort === "convosHandled"}           dir={filters.dir} align="right" />
                    <th className="px-2 py-2 text-right">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((e) => (
                    <tr key={`human-${e.id}`} className="text-app-text">
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar name={e.name} url={e.avatarUrl ?? null} size={24} />
                          <div className="font-medium truncate">{e.name}</div>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          <ToneDot pct={e.frAttainmentPct} goal={frGoal} />
                          <span className={attainmentToneClass(e.frAttainmentPct, frGoal)}>
                            {formatPct(e.frAttainmentPct)}
                          </span>
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          <ToneDot pct={e.resAttainmentPct} goal={resGoal} />
                          <span className={attainmentToneClass(e.resAttainmentPct, resGoal)}>
                            {formatPct(e.resAttainmentPct)}
                          </span>
                        </span>
                      </td>
                      <td className={`px-2 py-2 text-right ${e.idleBreaches > 0 ? "text-status-warning" : "text-app-muted"}`}>
                        {e.idleBreaches.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">{formatSeconds(e.avgFirstResponseSeconds)}</td>
                      <td className="px-2 py-2 text-right">{e.convosHandled.toLocaleString()}</td>
                      <td className="px-2 py-2 text-right">
                        <div style={{ width: 70 }} className="ml-auto">
                          <Sparkline
                            values={e.sparkline ?? []}
                            height={20}
                            variant="line"
                            colorClassName={trendColorClass(e.frAttainmentPct, frGoal)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ─── Pagination ──────────────────────────────────────────── */}
        <div className="flex items-center justify-between text-xs text-app-muted">
          <div>
            {pagination.totalRows === 0
              ? "No handlers"
              : `Showing ${start}-${end} of ${pagination.totalRows.toLocaleString()} handlers`}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => gotoPage(pagination.page - 1)}
            >
              ← Prev
            </Button>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => gotoPage(pagination.page + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  )
}

// ─── Subcomponents ───────────────────────────────────────────────────

function RangeSwitcher({ activeKey, onChange }: { activeKey: RangeKey; onChange: (k: RangeKey) => void }) {
  return (
    <div className="flex items-center gap-1">
      {RANGES.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onChange(r.key)}
          className={
            "rounded-control border px-2.5 py-1 text-[11px] " +
            (r.key === activeKey
              ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
              : "border-border bg-white/5 text-app-muted hover:text-app-text")
          }
        >
          {r.label}
        </button>
      ))}
    </div>
  )
}

function SortHeader({
  label, active, dir, align, onClick,
}: { label: string; active: boolean; dir: "asc" | "desc"; align: "left" | "right"; onClick: () => void }) {
  const arrow = !active ? "" : dir === "desc" ? "↓" : "↑"
  return (
    <th className={`px-2 py-2 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-app-text ${active ? "text-app-text" : ""}`}
      >
        {label}
        <span className="text-[10px] opacity-70">{arrow}</span>
      </button>
    </th>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────

function attainmentToneClass(pct: number | null, goal: number | null): string {
  if (pct == null) return "text-app-muted"
  if (goal == null) return "text-app-text"
  if (pct >= goal) return "text-status-success"
  if (pct >= goal - 5) return "text-status-warning"
  return "text-status-error"
}

function trendColorClass(pct: number | null, goal: number | null): string {
  if (pct == null || goal == null) return "text-app-muted"
  if (pct >= goal) return "text-status-success"
  if (pct >= goal - 5) return "text-status-warning"
  return "text-status-error"
}

function ToneDot({ pct, goal }: { pct: number | null; goal: number | null }) {
  if (pct == null) return null
  let bg = "bg-app-muted"
  if (goal != null) {
    if (pct >= goal) bg = "bg-status-success"
    else if (pct >= goal - 5) bg = "bg-status-warning"
    else bg = "bg-status-error"
  }
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${bg}`} aria-hidden />
}

function targetFootnote(
  goal: number | null | undefined,
  cmp: "gte" | "lte",
  fmt: (n: number | null) => string,
): string {
  if (goal == null) return "No SLA target"
  const op = cmp === "gte" ? "≥" : "≤"
  return `🎯 Target ${op} ${fmt(goal)}`
}

function priorLabelFor(rangeKey: RangeKey): string {
  switch (rangeKey) {
    case "today":  return "vs prior day"
    case "7d":     return "vs prior 7 days"
    case "30d":    return "vs prior 30 days"
    case "90d":    return "vs prior 90 days"
    case "custom": return "vs prior period"
  }
}

function formatPct(n: number | null): string {
  if (n == null) return "—"
  return `${n.toFixed(1)}%`
}

function formatSeconds(n: number | null): string {
  if (n == null) return "—"
  if (n < 60) return `${Math.round(n)}s`
  const minutes = Math.floor(n / 60)
  const secs = Math.round(n % 60)
  if (minutes < 60) return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

function formatCount(n: number | null): string {
  if (n == null) return "—"
  return n.toLocaleString()
}
